import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, ArrowRight, CheckCircle, MapPin, Play, Crown, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TournamentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [myRounds, setMyRounds] = useState([]);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/tournaments/${id}`);
    setT(data);
  };
  const [partnerEmail, setPartnerEmail] = useState("");
  const [teamName, setTeamName] = useState("");
  const [friends, setFriends] = useState([]);
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => { api.get("/rounds").then((r) => setMyRounds(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/friends").then((r) => setFriends(r.data)).catch(() => {}); }, []);

  if (!t) return <div className="py-10">Loading…</div>;

  const isCreator = user?.id === t.creator_id;
  const me = t.leaderboard.find((p) => p.user_id === user?.id);
  const joined = !!me;
  const currentSubmittedByMe = me?.scores.some((s) => s.round_num === t.current_round);

  const eligibleRounds = myRounds.filter((r) =>
    r.course_id === t.course_id && r.tees === t.tee_name &&
    !me?.scores.some((s) => s.round_id === r.id)
  );

  const join = async () => { await api.post(`/tournaments/${id}/join`); load(); toast.success("Joined"); };
  const leave = async () => { await api.post(`/tournaments/${id}/leave`); load(); toast.success("Left"); };
  const start = async () => { await api.post(`/tournaments/${id}/start`); load(); toast.success("Tournament started"); };
  const advance = async () => { await api.post(`/tournaments/${id}/advance`); load(); toast.success("Advanced"); };
  const del = async () => {
    if (!window.confirm("Delete this tournament?")) return;
    await api.delete(`/tournaments/${id}`);
    toast.success("Deleted"); navigate("/tournaments");
  };
  const submitScore = async (rid) => {
    setBusy(true);
    try {
      await api.post(`/tournaments/${id}/rounds/${t.current_round}/submit`, { round_id: rid });
      toast.success("Score submitted");
      setPicking(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // 4-ball-stableford team helpers
  const isTeamFormat = t.is_team_format;
  const myTeam = isTeamFormat ? (t.teams || []).find((tm) => tm.player_ids.includes(user?.id)) : null;
  const partnerOptions = friends.filter((f) =>
    t.leaderboard.some((p) => p.user_id === f.id) &&
    !(t.teams || []).some((tm) => tm.player_ids.includes(f.id))
  );
  const createTeam = async () => {
    if (!teamName.trim() || !partnerEmail) {
      toast.error("Team name + partner required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/tournaments/${id}/teams`, { name: teamName.trim(), partner_id: partnerEmail });
      toast.success("Team registered");
      setTeamName(""); setPartnerEmail(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const disbandTeam = async (teamId) => {
    if (!window.confirm("Disband this team?")) return;
    await api.delete(`/tournaments/${id}/teams/${teamId}`); load(); toast.success("Team disbanded");
  };

  return (
    <div className="space-y-6">
      <div className="relative h-44 md:h-56 rounded-2xl overflow-hidden">
        {t.course_image && <img src={t.course_image} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2537]/95 via-[#0F2537]/40 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 p-5 md:p-6 text-white">
          <div className="text-xs label-eyebrow !text-white/70 flex items-center gap-1">
            <Trophy size={11} weight="fill" /> {t.status.replace("_"," ").toUpperCase()} · R{t.current_round}/{t.rounds_count}
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">{t.name}</h1>
          <div className="text-sm text-white/80 mt-1 flex items-center gap-1"><MapPin size={11} weight="fill" /> {t.course_name} · {t.tee_name} · {t.format === "fourball_stableford" ? "4-Ball Stableford" : t.format}</div>
        </div>
      </div>

      {t.prizes?.length > 0 && (
        <div className="card-default p-5">
          <span className="label-eyebrow">Prizes</span>
          <ul className="mt-2 flex flex-wrap gap-2">
            {t.prizes.map((p) => (
              <li key={p.position} className="px-3 py-2 rounded-lg bg-[#F8F7F3] text-sm font-semibold inline-flex items-center gap-2">
                <Crown size={14} weight="fill" className="text-[#E05D3A]" /> {p.position}. {p.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {!joined && t.status === "registration" && (
          <button onClick={join} data-testid="t-join-btn" className="px-4 py-2 rounded-lg bg-[#E05D3A] text-white font-bold inline-flex items-center gap-1">Join <ArrowRight size={14} weight="bold" /></button>
        )}
        {joined && !isCreator && t.status === "registration" && (
          <button onClick={leave} data-testid="t-leave-btn" className="px-4 py-2 rounded-lg border border-[#E2E0D8] text-[#0F2537] font-bold">Leave</button>
        )}
        {isCreator && t.status === "registration" && (
          <button onClick={start} data-testid="t-start-btn" className="px-4 py-2 rounded-lg bg-[#0F2537] text-white font-bold inline-flex items-center gap-1"><Play size={14} weight="fill" /> Start tournament</button>
        )}
        {isCreator && t.status === "in_progress" && (
          <button onClick={advance} data-testid="t-advance-btn" className="px-4 py-2 rounded-lg bg-[#0F2537] text-white font-bold inline-flex items-center gap-1">
            {t.current_round >= t.rounds_count ? "Finish tournament" : "Advance to next round"} <ArrowRight size={14} weight="bold" />
          </button>
        )}
        {isCreator && (
          <button onClick={del} data-testid="t-delete-btn" className="px-3 py-2 rounded-lg border border-[#E2E0D8] text-[#0F2537]/70 hover:text-red-600 font-semibold text-sm">Delete</button>
        )}
      </div>

      {/* Teams panel — 4-ball-stableford only */}
      {isTeamFormat && (
        <div className="card-default p-5" data-testid="teams-panel">
          <span className="label-eyebrow">Teams ({(t.teams || []).length})</span>
          <h2 className="font-heading font-bold text-xl mt-1">Pair up</h2>
          <p className="text-xs text-[#0F2537]/60 mt-1">In 4-ball Better Ball Stableford, each player records their own round; the team's score on every hole is the <b>better</b> of the two teammates' Stableford points.</p>

          {(t.teams || []).length > 0 && (
            <ul className="mt-4 space-y-2">
              {(t.teams || []).map((tm) => (
                <li key={tm.team_id} className="p-3 rounded-xl bg-[#F8F7F3] flex items-center justify-between gap-3" data-testid={`team-row-${tm.team_id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-sm">{tm.team_name}</div>
                    <div className="text-xs text-[#0F2537]/60 truncate">{(tm.player_names || []).join(" + ")}</div>
                  </div>
                  {t.status === "registration" && (tm.player_ids.includes(user?.id) || isCreator) && (
                    <button onClick={() => disbandTeam(tm.team_id)} data-testid={`disband-team-${tm.team_id}`}
                      className="px-2 py-1 rounded-md text-xs font-bold text-red-600 hover:bg-red-50">Disband</button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Pair-up form — only when joined, in registration, not already paired */}
          {joined && t.status === "registration" && !myTeam && (
            <div className="mt-4 p-4 rounded-xl border border-dashed border-[#E2E0D8]" data-testid="pair-up-form">
              <div className="text-sm font-semibold mb-2">Pair with a friend</div>
              {partnerOptions.length === 0 ? (
                <div className="text-xs text-[#0F2537]/60">
                  No eligible partners — your friends must <b>join</b> the tournament first, then they'll appear here.
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input value={teamName} onChange={(e) => setTeamName(e.target.value)} data-testid="team-name-input"
                      placeholder="Team name (e.g. Bula Bombers)" className="px-3 py-2 rounded-lg border border-[#E2E0D8] bg-white text-sm" />
                    <select value={partnerEmail} onChange={(e) => setPartnerEmail(e.target.value)} data-testid="team-partner-select"
                      className="px-3 py-2 rounded-lg border border-[#E2E0D8] bg-white text-sm">
                      <option value="">Pick a partner…</option>
                      {partnerOptions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                  <button onClick={createTeam} disabled={busy} data-testid="create-team-btn"
                    className="mt-3 px-4 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold disabled:opacity-60">
                    Register team
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Submit score (in-progress only) */}
      {joined && t.status === "in_progress" && !me.missed_cut && !currentSubmittedByMe && (
        <div className="card-default p-5" data-testid="submit-score-block">
          <span className="label-eyebrow">Round {t.current_round} · submit your score</span>
          {eligibleRounds.length === 0 ? (
            <div className="mt-3 p-5 rounded-xl bg-[#F8F7F3] flex items-start justify-between gap-3 flex-wrap" data-testid="no-eligible-round-cta">
              <div>
                <div className="font-bold">No qualifying round yet</div>
                <div className="text-sm text-[#0F2537]/70 mt-1">
                  Play 18 holes at <b>{t.course_name}</b> from the <b>{t.tee_name}</b> tees to submit.
                </div>
              </div>
              <Link to={`/rounds/play/${t.course_id}?tee=${t.tee_name}`} data-testid="play-eligible-round-btn"
                className="px-4 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold inline-flex items-center gap-1">
                <Play weight="fill" size={14} /> Play now
              </Link>
            </div>
          ) : !picking ? (
            <button onClick={() => setPicking(true)} data-testid="pick-round-btn"
              className="mt-3 px-4 py-2 rounded-lg bg-[#0F2537] text-white text-sm font-bold">Pick a round</button>
          ) : (
            <ul className="mt-3 space-y-2">
              {eligibleRounds.map((r) => (
                <li key={r.id} className="flex items-center justify-between p-3 border border-[#E2E0D8] rounded-lg">
                  <div>
                    <div className="font-semibold">{new Date(r.played_at).toLocaleDateString()} · {r.total_strokes} strokes ({r.total_strokes - r.total_par >= 0 ? "+" : ""}{r.total_strokes - r.total_par})</div>
                    <div className="text-xs text-[#0F2537]/60">{r.total_stableford ?? 0} stableford pts · diff {r.differential}</div>
                  </div>
                  <button onClick={() => submitScore(r.id)} disabled={busy} data-testid={`submit-round-${r.id}`}
                    className="px-3 py-1.5 rounded-lg bg-[#E05D3A] text-white text-sm font-bold disabled:opacity-60">Submit</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Team Leaderboard (4-ball-stableford only) */}
      {isTeamFormat && t.team_leaderboard && t.team_leaderboard.length > 0 && (
        <div className="card-default overflow-hidden" data-testid="team-leaderboard-card">
          <div className="px-5 pt-5"><span className="label-eyebrow">Team standings (better-ball)</span><h2 className="font-heading font-bold text-xl mt-1">4-Ball Stableford</h2></div>
          <table className="w-full text-sm mt-3" data-testid="team-leaderboard">
            <thead>
              <tr className="text-[#0F2537]/60 text-xs uppercase tracking-widest border-b border-[#E2E0D8]">
                <th className="text-left px-5 py-3">Pos</th>
                <th className="text-left px-5 py-3">Team</th>
                <th className="text-right px-5 py-3 hidden sm:table-cell">R</th>
                <th className="text-right px-5 py-3">Better-Ball Pts</th>
                <th className="text-right px-5 py-3">Prize</th>
              </tr>
            </thead>
            <tbody>
              {t.team_leaderboard.map((row) => {
                const meInRow = row.member_ids.includes(user?.id);
                return (
                  <tr key={row.team_id} className={`border-b border-[#E2E0D8] last:border-0 ${meInRow ? "bg-[#E05D3A]/8" : ""}`} data-testid={`team-lb-row-${row.team_id}`}>
                    <td className="px-5 py-3">
                      {row.rank ? (
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-heading font-black text-sm ${
                          row.rank === 1 ? "bg-[#E05D3A] text-white" :
                          row.rank <= 3 ? "bg-[#0F2537] text-white" :
                          "bg-[#F8F7F3] text-[#0F2537]"
                        }`}>{row.rank}</span>
                      ) : <span className="text-xs text-[#0F2537]/40">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-semibold">{row.team_name}{meInRow && <span className="ml-2 text-xs text-[#E05D3A]">You</span>}</div>
                      <div className="text-xs text-[#0F2537]/55 truncate">{(row.member_names || []).join(" + ")}</div>
                    </td>
                    <td className="px-5 py-3 text-right hidden sm:table-cell">{row.rounds_completed}/{t.rounds_count}</td>
                    <td className="px-5 py-3 text-right font-heading font-bold">{row.total_stableford}</td>
                    <td className="px-5 py-3 text-right text-xs font-semibold text-[#E05D3A]">{row.prize || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Leaderboard */}
      <div className="card-default overflow-hidden">
        <div className="px-5 pt-5"><span className="label-eyebrow">Leaderboard</span><h2 className="font-heading font-bold text-xl mt-1">Standings</h2></div>
        <table className="w-full text-sm mt-3" data-testid="t-leaderboard">
          <thead>
            <tr className="text-[#0F2537]/60 text-xs uppercase tracking-widest border-b border-[#E2E0D8]">
              <th className="text-left px-5 py-3">Pos</th>
              <th className="text-left px-5 py-3">Player</th>
              <th className="text-right px-5 py-3 hidden sm:table-cell">R</th>
              {t.format === "stableford" && <th className="text-right px-5 py-3">Pts</th>}
              <th className="text-right px-5 py-3">Strokes</th>
              <th className="text-right px-5 py-3 hidden md:table-cell">Diff</th>
              <th className="text-right px-5 py-3">Prize</th>
            </tr>
          </thead>
          <tbody>
            {t.leaderboard.map((row) => {
              const isMe = user?.id === row.user_id;
              return (
                <tr key={row.user_id} className={`border-b border-[#E2E0D8] last:border-0 ${isMe ? "bg-[#E05D3A]/8" : ""} ${row.missed_cut ? "opacity-50" : ""}`} data-testid={`t-row-${row.user_id}`}>
                  <td className="px-5 py-3">
                    {row.missed_cut ? (
                      <span className="inline-flex items-center gap-1 text-xs text-red-600 font-bold"><XCircle size={14} weight="fill" /> CUT</span>
                    ) : (
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-heading font-black text-sm ${
                        row.rank === 1 ? "bg-[#E05D3A] text-white" :
                        row.rank <= 3 ? "bg-[#0F2537] text-white" :
                        "bg-[#F8F7F3] text-[#0F2537]"
                      }`}>{row.rank}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-semibold">{row.user_name}{isMe && <span className="ml-2 text-xs text-[#E05D3A]">You</span>}</td>
                  <td className="px-5 py-3 text-right hidden sm:table-cell">{row.rounds_played}/{t.rounds_count}</td>
                  {t.format === "stableford" && <td className="px-5 py-3 text-right font-heading font-bold">{row.total_stableford}</td>}
                  <td className="px-5 py-3 text-right">{row.total_strokes || "—"}</td>
                  <td className="px-5 py-3 text-right hidden md:table-cell">{row.total_differential ? `${row.total_differential > 0 ? "+" : ""}${row.total_differential}` : "—"}</td>
                  <td className="px-5 py-3 text-right text-xs font-semibold text-[#E05D3A]">{row.prize || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {t.status === "completed" && (
        <div className="card-default p-6 text-center bg-[#0F2537] text-white">
          <CheckCircle size={28} weight="fill" className="text-[#E05D3A] mx-auto" />
          <div className="font-heading font-black text-xl mt-2">Tournament complete</div>
          {t.leaderboard[0] && <div className="text-white/80 mt-1">Winner: <b>{t.leaderboard[0].user_name}</b></div>}
        </div>
      )}

      <p className="text-sm text-[#0F2537]/60"><Link to="/tournaments" className="hover:text-[#E05D3A]">← All tournaments</Link></p>
    </div>
  );
}
